import { Receipt } from "lucide-react";
import { LegalCompanyInfo } from "@/components/LegalCompanyInfo";
import { useTosVersion } from "@/components/TermsBody";
import { useReleaseDateLabel } from "@/hooks/useReleaseMetadata";

/**
 * Compliance Roadmap Item 19 — Refund / Dispute Policy.
 *
 * The seven-category refund taxonomy from the spec. Everything here
 * echoes Terms §6 (All Sales Final) + §5 (Mint Recall credit
 * compensation) + §14 (Termination forfeiture) but in plain-English
 * user-facing form. Mandatory-law exception preserved verbatim.
 */
function S({ n, title, id, children }) {
  return (
    <section id={id} className="space-y-2.5">
      <h2 className="font-display text-lg text-white tracking-[0.04em] flex gap-2.5">
        <span className="text-cyan-300 font-mono">{n}.</span> {title}
      </h2>
      <div className="text-white/65 text-[13.5px] leading-relaxed space-y-2.5">{children}</div>
    </section>
  );
}

const Em = ({ children }) => <span className="text-white font-medium">{children}</span>;

export default function RefundsPage() {
  const version = useTosVersion();
  const releaseDate = useReleaseDateLabel();
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="refunds-page">
      <div className="hud-pill rounded-2xl p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-xl bg-cyan-400/15 border border-cyan-300/30 flex items-center justify-center shrink-0">
            <Receipt className="h-5 w-5 text-cyan-300" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">Legal · Payments &amp; Refunds</div>
            <h1 className="font-display text-3xl sm:text-4xl text-white tracking-[0.05em]">REFUND &amp; DISPUTE POLICY</h1>
          </div>
        </div>
        <p data-testid="refunds-current-version" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-8">
          Playden Inc. · Version {version} · {releaseDate}
        </p>

        <div className="space-y-8" data-testid="refunds-document">
          <div className="rounded-xl border border-cyan-400/30 bg-cyan-500/[0.05] p-4 text-[13px] leading-relaxed text-cyan-100/90">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-300/90 mb-1">The rule</div>
            <Em>All sales are final.</Em> Everything you buy, top up, open, commit to a Challenge Entry, put into a Community Prediction Pool, forge, reroll, ascend, redeem, forfeit, or lose in play is non-refundable, except as expressly listed in Section 1 below or as required by non-waivable applicable law. This mirrors Terms §6.
          </div>

          <S n="1" title="When you WILL receive compensation" id="eligible">
            <p>You will receive credit compensation, a replay, or a chargeback-honoured refund in the following narrow circumstances:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><Em>Mint Recall (Terms §5).</Em> If Playden recalls, disables, or retires a card you hold, you will be awarded adequate Credits as in-Service compensation determined in Playden&apos;s reasonable, good-faith discretion. Credit compensation is your sole and exclusive remedy.</li>
              <li><Em>Duplicate top-up.</Em> If your top-up is received twice for the same order due to a payment-network error, the duplicate will be credited (or, at the Operator&apos;s option, refunded to source).</li>
              <li><Em>Failed delivery of purchased Virtual Items.</Em> If you pay for a specific bundle (e.g., a credit pack) and the Service fails to deliver the credits/items purchased, we will deliver the missing items or issue an equivalent Credit adjustment.</li>
              <li><Em>Non-waivable statutory rights.</Em> Where a jurisdiction (e.g., certain EU/UK consumer statutes) grants you a non-waivable withdrawal/refund right, that right applies according to its terms.</li>
            </ul>
          </S>

          <S n="2" title="When you will NOT receive a refund" id="not-eligible">
            <p>The seven categories below are <Em>never</Em> grounds for refund, chargeback, restoration, or credit-back:</p>
            <ol className="list-decimal pl-5 space-y-1.5">
              <li><Em>Randomized-outcome dissatisfaction.</Em> A pack, reroll, forge, or ascension produces items of lower perceived value than the cost paid.</li>
              <li><Em>Competitive losses.</Em> You lose a Challenge Entry, a Community Prediction Pool commitment, an Arena match, a Cavern delve, a race, or an event, and Virtual Items (credits, cards, gear) are forfeited per that mode&apos;s in-Service rules.</li>
              <li><Em>Confirmed consumptive actions.</Em> You upgraded, reforged, ascended, burned, sacrificed, or otherwise consumed a Virtual Item — including actions taken by mistake, misclick, or in a rush.</li>
              <li><Em>Trades &amp; peer-to-peer transfers.</Em> You executed a trade you now regret. Trades are final on execution; fairness indicators are informational only.</li>
              <li><Em>Rebalances.</Em> Playden modified odds, stats, mechanics, or economy parameters going forward. Playden may rebalance at any time without compensation.</li>
              <li><Em>Termination for cause.</Em> Your account was suspended or terminated under Terms §14. All Virtual Items and balances are forfeited without compensation.</li>
              <li><Em>Buyer&apos;s remorse or change of mind.</Em> You changed your mind after purchase, or you find the Service or a particular feature is not what you expected.</li>
            </ol>
          </S>

          <S n="3" title="Cryptocurrency top-ups" id="crypto">
            <p>Cryptocurrency transactions are <Em>irreversible</Em> by nature. Once your top-up is confirmed on the network, we cannot un-send it. You bear all risk of network fees, exchange-rate movements, mis-sent funds, or transmission to an incorrect address. Playden will use commercially reasonable efforts to resolve genuine mis-sends but makes no guarantee that funds sent in error can be recovered.</p>
          </S>

          <S n="4" title="Manual Venmo top-ups" id="venmo">
            <p>Manual Venmo top-ups are credited only upon administrator verification of your note text against your account. If a top-up is not linked (missing or incorrect note), we will contact you through the Service to resolve the mismatch. Chargebacks initiated after credits have been delivered may result in immediate account suspension under Terms §14.</p>
          </S>

          <S n="5" title="Chargeback policy" id="chargebacks">
            <p>Filing a payment-network chargeback for any of the seven categories listed in Section 2 is bad-faith use of your payment provider&apos;s dispute rights and constitutes a material violation of these Terms. Playden reserves the right to (a) contest the chargeback with the payment network, (b) suspend or terminate your account under Terms §14, (c) reverse any Virtual Items obtained in connection with the disputed payment, and (d) pursue recovery of chargeback fees and legal costs.</p>
          </S>

          <S n="6" title="How to request eligible compensation" id="how-to">
            <p>If you believe your situation falls within Section 1 (Eligible), contact Playden through the Service&apos;s in-app support channel with:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Your account email and the order/transaction reference.</li>
              <li>A brief description of the issue and the outcome you expected.</li>
              <li>Any screenshots or blockchain transaction hashes that support your request.</li>
            </ul>
            <p>We aim to review requests within a reasonable time and will respond with our decision. Where credit compensation is the appropriate remedy under Terms §5, it will be delivered to your account balance.</p>
          </S>

          <S n="7" title="Non-waivable rights" id="rights">
            <p>Nothing in this Policy is intended to limit or waive any right you have that cannot be waived under the mandatory law of your place of habitual residence. Where such a right conflicts with a term of this Policy, that term is enforced to the maximum extent permissible and the mandatory right prevails as to the specific issue.</p>
          </S>

          <LegalCompanyInfo className="mt-4" testid="refunds-company-info" />
        </div>
      </div>
    </div>
  );
}
